import { useCallback, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { OpenAPI } from "simplydo/interfaces";
import { useThrottle } from "simplydo/react/hooks";
import { Button } from "simplydo/react/ui/button";
import { Dropdown } from "simplydo/react/ui/dropdown";
import { IconData } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { List, ListItem } from "simplydo/react/ui/list";
import { Loader } from "simplydo/react/ui/loader";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

import api from "api";
import { useAppSelector } from "store";

import ConfigurableTable from "../ConfigurableTable";

const IMPORTABLE_SECTION_TYPES = new Set(["custom", "edi", "assessment"]);

type IdeaTemplate = OpenAPI.Schemas["IdeaTemplate"];
type IdeaTemplateBody = NonNullable<IdeaTemplate["body"]>;

const isImportableSection = (section: IdeaTemplateBody[number]) =>
  !section.type || IMPORTABLE_SECTION_TYPES.has(section.type);

const getSectionImportKey = (challengeId: string, sectionId: string) =>
  `${challengeId}:${sectionId || "unknown-section"}`;

type TemplateFieldType = {
  key: string;
  name: string;
  type: string;
  icon?: IconData;
};

type ImportableSection = {
  importKey: string;
  id: string;
  name: string;
  type: string;
  sectionType: NonNullable<IdeaTemplateBody[number]["type"]>;
  assessmentRoundId?: string;
  challengeName: string;
  fields: IdeaTemplateBody[number]["fields"];
  disabled: boolean;
};

type SectionChooserProps = {
  isOpen: boolean;
  onClose: () => void;
  onImportSections: (sections: IdeaTemplateBody) => void;
  body: IdeaTemplateBody;
  challengeId: string;
  templateFieldTypes: TemplateFieldType[];
};

const SectionChooser = ({
  isOpen,
  onClose,
  onImportSections,
  body,
  challengeId,
  templateFieldTypes,
}: SectionChooserProps) => {
  const [searchLoading, setSearchLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [importableSections, setImportableSections] = useState<
    ImportableSection[]
  >([]);
  const [selectedSections, setSelectedSections] = useState<string[]>([]);
  const [searchFieldType, setSearchFieldType] = useState("");
  const { t } = useTranslation();

  const user = useAppSelector((state) => state.user);
  const userId = user?._id;

  const toggleSelection = (sectionImportKey: string) => {
    setSelectedSections((prev) =>
      prev.includes(sectionImportKey)
        ? prev.filter((id) => id !== sectionImportKey)
        : [...prev, sectionImportKey],
    );
  };

  const toImportableSections = useCallback(
    (
      challenges: (OpenAPI.Schemas["Challenge"] & {
        template?: IdeaTemplate;
      })[] = [],
    ) => {
      const currentChallengeFieldIds = new Set(
        body.reduce((ids, section) => {
          (section.fields || []).forEach((field) => {
            ids.push(field.id);
          });
          return ids;
        }, [] as string[]),
      );

      return challenges
        .filter((challenge) => challenge._id !== challengeId)
        .filter((challenge) => {
          const challengeBody = challenge.template?.body;
          return Array.isArray(challengeBody) && challengeBody.length > 0;
        })
        .reduce((allSections, challenge) => {
          const challengeBody = challenge.template?.body || [];

          const challengeSections = challengeBody
            .filter(isImportableSection)
            .map((section) => {
              const fields = (section.fields || []).map((field) => {
                return { ...field, challenge: challenge._id };
              });

              const hasImportedField = fields.some((field) =>
                currentChallengeFieldIds.has(field.id),
              );

              return {
                importKey: getSectionImportKey(challenge._id, section.id),
                id: section.id,
                name: section.name || "Untitled section",
                type: "section",
                sectionType: section.type!,
                assessmentRoundId: section.assessmentRoundId,
                challengeName: challenge.name,
                fields,
                disabled: hasImportedField,
              };
            });

          return [...allSections, ...challengeSections];
        }, [] as ImportableSection[]);
    },
    [body, challengeId],
  );

  const throttledFetch = useThrottle(api.users.getChallenges);
  const fetchImportableSections = useCallback(
    (query: string = "") => {
      if (!userId) return;

      setSearchLoading(true);
      throttledFetch(
        userId,
        { page: 1, limit: 10, query, forType: "ideaTemplate" },
        (data) => {
          setSearchLoading(false);
          setLoading(false);
          const sections = toImportableSections(data.challenges || []);
          setImportableSections(sections);
        },
        () => {
          setLoading(false);
          setSearchLoading(false);
        },
      );
    },
    [throttledFetch, userId, toImportableSections],
  );

  useEffect(() => {
    if (!isOpen) return;

    const timeoutId = setTimeout(() => {
      setLoading(true);
      fetchImportableSections();
    }, 0);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [fetchImportableSections, isOpen]);

  const handleImport = () => {
    const sectionsToImport = importableSections
      .filter((item) => selectedSections.includes(item.importKey))
      .map((item) => ({
        id: item.id,
        name: item.name,
        fields: item.fields,
        type: item.sectionType,
        assessmentRoundId: item.assessmentRoundId,
      }));

    if (sectionsToImport.length > 0) {
      const copiedSections: (IdeaTemplateBody[number] & {
        fields: IdeaTemplateBody[number]["fields"];
      })[] = [];
      let completed = 0;
      sectionsToImport.forEach((section, index) => {
        api.challenges.copyFields(
          challengeId,
          { fields: section.fields },
          ({ copiedFields }) => {
            copiedSections[index] = { ...section, fields: copiedFields };
            completed += 1;

            if (completed === sectionsToImport.length) {
              onImportSections(copiedSections);
            }
          },
          () => {},
        );
      });
    }
    onClose();
    setSelectedSections([]);
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <ModalHeader>Import an existing section</ModalHeader>
      <ModalContent scrolling>
        {loading && importableSections.length === 0 ? (
          <Loader>Loading sections...</Loader>
        ) : (
          <>
            <div style={{ display: "flex", gap: "1px", marginBottom: "10px" }}>
              <div style={{ marginBottom: "5px", flex: 1, minWidth: "200px" }}>
                <Input
                  loading={searchLoading}
                  placeholder={`Section or ${t("generic.challenge")} name`}
                  onChange={(e) => fetchImportableSections(e.target.value)}
                />
              </div>
              <div style={{ marginBottom: "5px", minWidth: "200px" }}>
                <Dropdown
                  placeholder="Filter by field type"
                  clearable
                  variant="outline"
                  options={templateFieldTypes
                    .filter((type) => type?.type === "general")
                    .map((type) => ({
                      key: type.key,
                      value: type.key,
                      text: type.name,
                    }))}
                  value={searchFieldType}
                  onChange={(_e, { value }) =>
                    setSearchFieldType(value as string)
                  }
                />
              </div>
            </div>

            {importableSections.length === 0 ? (
              <p>No sections available to import.</p>
            ) : (
              <ConfigurableTable
                tableKey="challengeSectionImport"
                loading={searchLoading}
                selectedKeys={selectedSections}
                keyExtractor={(item) => item.importKey}
                onSelect={(item) => {
                  if (item) {
                    toggleSelection(item.importKey);
                  }
                }}
                preventSelectAll
                columns={[
                  { key: "challengeName", name: "Challenge" },
                  { key: "name", name: "Section" },
                  {
                    key: "fields",
                    name: "Fields",
                    render: ({ item }) => (
                      <div
                        style={{
                          maxHeight: 100,
                          overflowY: "auto",
                          paddingRight: 0,
                          marginRight: -10,
                          boxSizing: "content-box",
                          width: "100%",
                        }}
                      >
                        {item.disabled && (
                          <em
                            style={{
                              color: "red",
                              display: "block",
                              marginBottom: 4,
                            }}
                          >
                            This section contains fields that have already been
                            added
                          </em>
                        )}
                        <List
                          variant="bulleted"
                          compact
                          style={{ margin: 0, paddingLeft: "1em" }}
                        >
                          {item.fields.length > 0 ? (
                            item.fields.map((field) => (
                              <ListItem
                                key={field.id}
                                style={{ marginBottom: 4 }}
                              >
                                {field.title || "Untitled Field"}
                              </ListItem>
                            ))
                          ) : (
                            <ListItem>No fields</ListItem>
                          )}
                        </List>
                      </div>
                    ),
                  },
                ]}
                data={importableSections}
              />
            )}
          </>
        )}
      </ModalContent>
      <ModalActions>
        <Button
          onClick={() => {
            onClose();
            setSelectedSections([]);
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            handleImport();
          }}
          disabled={selectedSections.length === 0}
          color="primary"
        >
          Import {selectedSections.length} Section
          {selectedSections.length !== 1 ? "s" : ""}
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default SectionChooser;
